import InputErrorMessage from "@/components/InputErrorMessage"
import { CheckboxInput, DateInput, SelectInput } from "@/components/InputFields"
import InputLabel from "@/components/InputLabel"
import { Pregnancy } from "@/model/Pregnancy"
import { formatDate } from "@/utils/date-utils"
import { LocalDate } from "@js-joda/core"
import { ChangeEvent } from "react"
import {
  FieldError,
  FieldValues,
  UseFormRegister,
  UseFormSetValue,
} from "react-hook-form"
import { newPregnancyDefault, PregnancySelection } from "./PregnancySelection"

export type PregnancyFieldErrors = {
  pregnancyId?: FieldError
  estimatedDateOfDelivery?: FieldError
}

type PregnancySelectorProps<T extends FieldValues> = {
  pregnancies: Pregnancy[]
  pregnancy: PregnancySelection
  canAddNewPregnancy: boolean
  requireEstimatedDateOfDelivery: boolean
  register: UseFormRegister<T>
  setValue: UseFormSetValue<T>
  errors: PregnancyFieldErrors
}

const ADD_NEW_OPTION = "__add_new__"

const labelForPregnancy = (pregnancy: Pregnancy): string => {
  if (pregnancy.status === "delivered" && pregnancy.deliveryDate) {
    return `Delivered ${formatDate(pregnancy.deliveryDate, "MM/dd/yyyy")}`
  }
  if (pregnancy.estimatedDateOfDelivery) {
    return `EDD ${formatDate(pregnancy.estimatedDateOfDelivery, "MM/dd/yyyy")}`
  }
  return "No EDD set"
}

const PregnancySelector = <T extends FieldValues>({
  pregnancies,
  pregnancy,
  canAddNewPregnancy,
  requireEstimatedDateOfDelivery,
  register: registerParent,
  setValue: setValueParent,
  errors,
}: PregnancySelectorProps<T>) => {
  // RHF's UseFormRegister/SetValue are invariant in TFieldValues, so cast
  // locally to the slice this component manages — the only invariant the
  // selector needs is that the parent form carries a `pregnancy` field of
  // type PregnancySelection.
  const register = registerParent as unknown as UseFormRegister<{
    pregnancy: PregnancySelection
  }>
  const setValue = setValueParent as unknown as UseFormSetValue<{
    pregnancy: PregnancySelection
  }>

  const selectValue =
    pregnancy.kind === "new" ? ADD_NEW_OPTION : pregnancy.pregnancyId

  const handleSelectChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value
    if (value === ADD_NEW_OPTION) {
      setValue("pregnancy", newPregnancyDefault, { shouldValidate: true })
    } else {
      setValue(
        "pregnancy",
        { kind: "existing", pregnancyId: value },
        { shouldValidate: true }
      )
    }
  }

  const options = [
    { title: "-- Select a pregnancy --", value: "", disabled: true },
    ...pregnancies.map((pregnancy) => ({
      value: pregnancy.id,
      title: labelForPregnancy(pregnancy),
    })),
    {
      value: ADD_NEW_OPTION,
      title: "Add new pregnancy",
      disabled: !canAddNewPregnancy,
    },
  ]

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <InputLabel htmlFor="pregnancy-choice" className="ml-1">
          Pregnancy
        </InputLabel>
        <SelectInput
          id="pregnancy-choice"
          options={options}
          value={selectValue}
          onChange={handleSelectChange}
        />
        <InputErrorMessage
          message={errors.pregnancyId?.message}
          className="ml-1"
        />
        {!canAddNewPregnancy && (
          <p className="ml-1 text-xs text-primary/60">
            Close all active prescriptions before adding a new pregnancy.
          </p>
        )}
      </div>

      {pregnancy.kind === "new" && (
        <div className="flex flex-col gap-4 rounded-xl border border-primary/15 p-4">
          <div className="grid grid-cols-2 gap-8">
            <div className="flex flex-col gap-1">
              <InputLabel htmlFor="new-pregnancy-edd" className="ml-1">
                Estimated delivery date
                {requireEstimatedDateOfDelivery ? "*" : ""}
              </InputLabel>
              <DateInput
                id="new-pregnancy-edd"
                isError={!!errors.estimatedDateOfDelivery?.message}
                {...register("pregnancy.estimatedDateOfDelivery")}
              />
              <InputErrorMessage
                message={errors.estimatedDateOfDelivery?.message}
                className="ml-1"
              />
            </div>

            <div className="flex flex-col gap-1">
              <InputLabel
                htmlFor="new-pregnancy-delivery-date"
                className="ml-1"
              >
                Delivery date
              </InputLabel>
              <DateInput
                id="new-pregnancy-delivery-date"
                max={LocalDate.now().toString()}
                {...register("pregnancy.deliveryDate")}
              />
            </div>
          </div>

          <div className="flex flex-row items-center gap-2">
            <CheckboxInput
              id="new-pregnancy-opted-in"
              {...register("pregnancy.patientOptedInClinically")}
            />
            <InputLabel htmlFor="new-pregnancy-opted-in" className="ml-1">
              Patient opted in clinically
            </InputLabel>
          </div>
        </div>
      )}
    </div>
  )
}

export default PregnancySelector
